/**
 * Reading the weekly pipeline series — what is moving, and what that means.
 *
 * Theresa asked for ideas here: *"Are application numbers going down? Are we interviewing more /
 * less than last weeks, are any important KPIs going up / down?"* The counts come from the
 * warehouse; everything below is the judgement, client-side and testable without a network, the
 * same split the radar's traffic lights take.
 *
 * ⚠️ THE WINDOWS ARE ROLLING 7-DAY PERIODS, not calendar weeks, and that is what makes a
 * week-on-week read honest. A calendar week in progress is a partial bucket, so a dashboard built
 * on them shows every trend falling for six days out of seven and recovering on Sundays. Every
 * point here covers a full seven days ending at a fixed offset from now.
 *
 * ⚠️ NO CONVERSION RATES, DELIBERATELY. The obvious next step — first interviews ÷ applications
 * over the same fortnight — divides two different cohorts: an application this week becomes a
 * first interview in two or three. The ratio moves for reasons that have nothing to do with
 * conversion, and it moves most when volume is changing, which is exactly when it would be read.
 * `readShape` gets the same insight out of the LAG instead of fighting it: which stages moved, and
 * in what order.
 */
import type { PipelineTrends, TrendMilestoneKey } from '../types/pipelineTrends';

/** Weeks per comparison window. Two keeps a single quiet week from reading as a trend. */
export const WINDOW_WEEKS = 2;

/** Below this many in the PRIOR window, a ratio is noise whatever it says. */
export const MIN_PRIOR = 5;

/** Movement inside this band is the same fortnight twice. */
export const DEADBAND = 0.15;

export type Direction = 'up' | 'down' | 'flat' | 'unknown';

export interface Movement {
  key: TrendMilestoneKey;
  label: string;
  recent: number;
  prior: number;
  /** recent ÷ prior. Null when the prior window is too thin to divide by. */
  ratio: number | null;
  /** Percentage change, rounded. Null with the ratio. */
  changePct: number | null;
  direction: Direction;
  /** The last `TREND_WEEKS` counts, oldest first — what the sparkline draws. */
  series: number[];
}

function sumWindow(weeks: PipelineTrends['weeks'], key: TrendMilestoneKey, from: number): number {
  return weeks
    .filter((w) => w.weeksAgo >= from && w.weeksAgo < from + WINDOW_WEEKS)
    .reduce((sum, w) => sum + (w[key] ?? 0), 0);
}

export function movements(trends: PipelineTrends | null | undefined): Movement[] {
  if (!trends) return [];

  return trends.milestones.map(({ key, label }) => {
    const recent = sumWindow(trends.weeks, key, 0);
    const prior = sumWindow(trends.weeks, key, WINDOW_WEEKS);
    const ratio = prior >= MIN_PRIOR ? recent / prior : null;

    return {
      key,
      label,
      recent,
      prior,
      ratio,
      changePct: ratio == null ? null : Math.round((ratio - 1) * 100),
      direction:
        ratio == null ? 'unknown' : ratio > 1 + DEADBAND ? 'up' : ratio < 1 - DEADBAND ? 'down' : 'flat',
      // Oldest first, so the sparkline reads left to right like every other chart on the tab.
      series: [...trends.weeks].sort((a, b) => b.weeksAgo - a.weeksAgo).map((w) => w[key] ?? 0),
    };
  });
}

/* --------------------------------------------------------------- prospects: momentum */

export interface Momentum {
  verdict: 'gaining' | 'holding' | 'slowing' | 'unknown';
  /** The EARLIEST stage that is slowing — what to act on, not the worst one. */
  firstSlowing: Movement | null;
  reason: string;
}

/**
 * The stages that decide THIS trimester's hires. Applications matter too, and they are the Trends
 * tile's business — an application today is a hire next quarter, and folding it in here would let
 * a good sourcing week mask offers drying up.
 */
const MOMENTUM_STAGES: TrendMilestoneKey[] = ['firstInterviews', 'caseInterviews', 'offers'];

/**
 * ⚠️ THE EARLIEST SLOWING STAGE IS WHAT GETS NAMED, not the worst — the same rule the radar's
 * bottleneck follows. A funnel is a sequence, and chasing a late stage while an earlier one is
 * starving it achieves nothing.
 *
 * ⚠️ The judgeable ratios are averaged with EQUAL weight. Weighting late stages higher is
 * defensible and was rejected: the weights would be invented, nobody could check them, and the
 * arrow would move for reasons no one could trace back to a number on the page.
 */
export function momentum(moves: Movement[]): Momentum {
  const staged = MOMENTUM_STAGES.map((k) => moves.find((m) => m.key === k)).filter(
    (m): m is Movement => Boolean(m)
  );
  const judgeable = staged.filter((m) => m.ratio != null);

  if (!judgeable.length) {
    return {
      verdict: 'unknown',
      firstSlowing: null,
      reason: 'Too few interviews and offers in the last four weeks to read a direction.',
    };
  }

  const mean = judgeable.reduce((s, m) => s + (m.ratio as number), 0) / judgeable.length;
  const firstSlowing = staged.find((m) => m.direction === 'down') ?? null;

  const verdict = mean > 1 + DEADBAND ? 'gaining' : mean < 1 - DEADBAND ? 'slowing' : 'holding';
  const movedUp = staged.filter((m) => m.direction === 'up');

  const reason = firstSlowing
    ? `${firstSlowing.label} are ${Math.abs(firstSlowing.changePct ?? 0)}% down on the fortnight before — ${firstSlowing.recent} against ${firstSlowing.prior}.`
    : movedUp.length
      ? `${movedUp[0].label} are ${movedUp[0].changePct}% up on the fortnight before.`
      : 'Interviews and offers are running at the same rate as the fortnight before.';

  return { verdict, firstSlowing, reason };
}

/* ------------------------------------------------------------------ trends: the shape */

export type ShapeKind = 'volume' | 'conversion' | 'incoming' | 'steady' | 'unknown';

export interface TrendShape {
  kind: ShapeKind;
  headline: string;
  detail: string;
}

const EARLY: TrendMilestoneKey[] = ['applications', 'firstInterviews'];
const LATE: TrendMilestoneKey[] = ['caseInterviews', 'offers', 'accepted'];

/**
 * What the pattern across the funnel is saying.
 *
 * ⚠️ THIS IS WHERE THE LAG EARNS ITS KEEP. A conversion rate over one fortnight divides two
 * different cohorts and is least trustworthy exactly when volume is moving. The ORDER in which
 * stages move says the same thing and needs no division:
 *
 *   everything down            volume — the top of the funnel is the cause, and it is already
 *                              working through the rest
 *   early flat, late down      not volume. Candidates are reaching the stage and not getting past
 *                              it, or decisions are not being made
 *   early down, late flat      the slowdown has not arrived yet — the late stages are still
 *                              working through a cohort that applied weeks ago. This is the one
 *                              worth catching, because everything still looks fine
 *
 * Measured on this warehouse: the median case interview turns into an offer in 6 days, and an
 * application reaches a first interview in rather longer — which is why "early" and "late" are
 * split where they are.
 */
export function trendShape(moves: Movement[]): TrendShape {
  const pick = (keys: TrendMilestoneKey[]) =>
    moves.filter((m) => keys.includes(m.key) && m.direction !== 'unknown');

  const early = pick(EARLY);
  const late = pick(LATE);
  if (!early.length || !late.length) {
    return {
      kind: 'unknown',
      headline: 'Not enough of the funnel is readable to call a shape.',
      detail: 'Some stages had too few candidates in the last four weeks to compare.',
    };
  }

  const down = (ms: Movement[]) => ms.filter((m) => m.direction === 'down');
  const earlyDown = down(early);
  const lateDown = down(late);

  if (earlyDown.length && lateDown.length) {
    return {
      kind: 'volume',
      headline: 'Fewer candidates all the way through.',
      detail: `${earlyDown.map((m) => m.label.toLowerCase()).join(' and ')} are down, and so ${lateDown.length === 1 ? 'is' : 'are'} ${lateDown.map((m) => m.label.toLowerCase()).join(' and ')}. The top of the funnel is the cause and it has already reached the rest — more pipeline is what moves this, not faster decisions.`,
    };
  }

  if (!earlyDown.length && lateDown.length) {
    return {
      kind: 'conversion',
      headline: 'Candidates are arriving but not getting through.',
      detail: `${lateDown.map((m) => m.label.toLowerCase()).join(' and ')} are down while applications and first interviews held up. That is not a volume problem: people are reaching the stage and not passing it, or the decision is not being made. Worth looking at the stage itself rather than at sourcing.`,
    };
  }

  if (earlyDown.length && !lateDown.length) {
    return {
      kind: 'incoming',
      headline: 'The slowdown has not arrived yet.',
      detail: `${earlyDown.map((m) => m.label.toLowerCase()).join(' and ')} are down while the later stages are still holding — they are working through candidates who applied weeks ago. This is the one to catch early: by the time offers move, the cause is a month old.`,
    };
  }

  return {
    kind: 'steady',
    headline: 'Nothing is falling.',
    detail: 'No stage is materially down on the fortnight before.',
  };
}
